import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaCheck, FaCoins, FaLock, FaExternalLinkAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import api from '../../api/axios';

const CHANNELS = [
  { code: 'WAVECI',  label: 'Wave',             pays: '🇨🇮 Côte d\'Ivoire', logo: null             },
  { code: 'OMCIV2',  label: 'Orange Money',      pays: '🇨🇮 Côte d\'Ivoire', logo: '/logo-om.png'   },
  { code: 'MOMOCI',  label: 'MTN Mobile Money',  pays: '🇨🇮 Côte d\'Ivoire', logo: '/logo-mtn.jpg'  },
  { code: 'FLOOZ',   label: 'Moov Money',        pays: '🇨🇮 Côte d\'Ivoire', logo: '/logo-moov.png' },
  { code: 'WAVESN',  label: 'Wave',              pays: '🇸🇳 Sénégal',        logo: null             },
  { code: 'OMSN',    label: 'Orange Money',      pays: '🇸🇳 Sénégal',        logo: '/logo-om.png'   },
  { code: 'OMCM',    label: 'Orange Money',      pays: '🇨🇲 Cameroun',       logo: '/logo-om.png'   },
  { code: 'MOMOCM',  label: 'MTN Mobile Money',  pays: '🇨🇲 Cameroun',       logo: '/logo-mtn.jpg'  },
  { code: 'OMBF',    label: 'Orange Money',      pays: '🇧🇫 Burkina Faso',   logo: '/logo-om.png'   },
  { code: 'OMML',    label: 'Orange Money',      pays: '🇲🇱 Mali',            logo: '/logo-om.png'   },
  { code: 'MOMOBJ',  label: 'MTN Mobile Money',  pays: '🇧🇯 Bénin',          logo: '/logo-mtn.jpg'  },
  { code: 'FLOOZBJ', label: 'Moov Money',        pays: '🇧🇯 Bénin',          logo: '/logo-moov.png' },
  { code: 'MOOVTG',  label: 'Flooz',             pays: '🇹🇬 Togo',           logo: '/logo-moov.png' },
  { code: 'TOGOCEL', label: 'Togo Cel',          pays: '🇹🇬 Togo',           logo: null             },
  { code: 'AIRTELNG',label: 'Airtel',            pays: '🇳🇪 Niger',           logo: null             },
  { code: 'OMGN',    label: 'Orange Money',      pays: '🇬🇼 Guinée-Bissau',  logo: '/logo-om.png'   },
];

function CoinPaymentModal({ open, onClose, pack }) {
  const [channel, setChannel] = useState('WAVECI');
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    setLoading(true);
    try {
      const { data } = await api.post('/payments/paiementpro/coins', {
        packId: pack.id,
        channel,
      });
      if (!data.success || !data.url) {
        toast.error('Impossible de démarrer le paiement. Réessaie.');
        return;
      }
      window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erreur lors de l\'initialisation du paiement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && pack && (
        <motion.div
          className="fixed inset-0 z-50 flex items-end justify-center"
          style={{ paddingBottom: 72 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            className="relative w-full max-w-md rounded-3xl flex flex-col"
            style={{
              background: '#0d2b1a',
              border: '1px solid rgba(240,180,41,0.2)',
              maxHeight: 'calc(85vh - 72px)',
            }}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Contenu scrollable */}
            <div className="overflow-y-auto px-5 pt-2 pb-4 flex-1 min-h-0">

              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-black text-qfl-gold uppercase tracking-widest"
                  style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  <FaCoins className="inline mr-2 mb-1" size={16} />
                  Achat de Pièces
                </h2>
                <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/10 transition-colors">
                  <FaTimes size={16} className="text-white/60" />
                </button>
              </div>

              {/* Pack sélectionné */}
              <div className="flex items-center justify-between rounded-xl px-4 py-3 mb-4"
                style={{ background: 'rgba(240,180,41,0.1)', border: '1px solid rgba(240,180,41,0.3)' }}>
                <div>
                  <div className="text-white/50 text-xs">Pack sélectionné</div>
                  <div className="text-white font-black text-base" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                    {pack.label}
                  </div>
                  <div className="text-qfl-gold/70 text-xs">{pack.coins?.toLocaleString()} pièces</div>
                </div>
                <div className="text-qfl-gold font-black text-xl" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                  {pack.amountLabel}
                </div>
              </div>

              {/* Sélecteur réseau */}
              <div className="mb-4">
                <p className="text-white/40 text-xs mb-2 uppercase tracking-wider">Choisir le moyen de paiement</p>
                <div className="flex flex-col gap-2 max-h-52 overflow-y-auto pr-1">
                  {CHANNELS.map((ch) => (
                    <button
                      key={ch.code}
                      onClick={() => setChannel(ch.code)}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-all"
                      style={{
                        background: channel === ch.code ? 'rgba(240,180,41,0.12)' : 'rgba(255,255,255,0.03)',
                        border: channel === ch.code ? '1px solid rgba(240,180,41,0.4)' : '1px solid rgba(255,255,255,0.08)',
                      }}
                    >
                      {ch.logo ? (
                        <div className="w-8 h-8 rounded-lg overflow-hidden bg-white flex-shrink-0 flex items-center justify-center">
                          <img src={ch.logo} alt={ch.label} className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center text-base"
                          style={{ background: 'rgba(255,255,255,0.08)' }}>
                          💳
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-sm font-bold" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
                          {ch.label}
                        </div>
                        <div className="text-white/35 text-xs">{ch.pays}</div>
                      </div>
                      {channel === ch.code && <FaCheck size={12} className="text-qfl-gold flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>

              {/* Info sécurité */}
              <div className="rounded-xl p-3 flex items-center gap-2"
                style={{ background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)' }}>
                <FaLock size={11} className="text-green-400 flex-shrink-0" />
                <p className="text-white/40 text-xs leading-relaxed">
                  Passerelle sécurisée <strong className="text-white/60">PaiementPro</strong> — pièces créditées automatiquement après paiement.
                </p>
              </div>
            </div>

            {/* Bouton PAYER — collé en bas */}
            <div className="px-5 pb-5 pt-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
              <button
                onClick={handlePay}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-4 rounded-xl font-black text-sm uppercase tracking-widest transition-opacity disabled:opacity-60"
                style={{
                  background: loading ? 'rgba(240,180,41,0.5)' : 'linear-gradient(135deg, #f0b429, #c97d08)',
                  color: '#0d2b1a',
                  fontFamily: "'Rajdhani', sans-serif",
                }}
              >
                {loading ? (
                  <><div className="w-4 h-4 border-2 border-qfl-dark border-t-transparent rounded-full animate-spin" />Initialisation...</>
                ) : (
                  <><FaExternalLinkAlt size={14} />PAYER {pack.amountLabel}</>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default CoinPaymentModal;
